import { Suspense } from 'react';
import { createApiRef } from '@backstage/core-plugin-api';
import techdocsPlugin from '@backstage/plugin-techdocs/alpha';
import { techdocsPlugin as legacyTechdocsPlugin } from '@backstage/plugin-techdocs';
import { TechDocsAddonLocations } from '@backstage/plugin-techdocs-react';
import {
  AddonBlueprint,
  attachTechDocsAddonComponentData,
  type TechDocsAddonOptions,
} from '@backstage/plugin-techdocs-react/alpha';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { techdocsMermaidProps } from '../components/techdocs/mermaidConfig';
import { TechDocsNavScrollFixAddon } from '../components/techdocs/TechDocsNavScrollFix';

/** Same id as @backstage/plugin-techdocs `api:techdocs/addons` (not re-exported from /alpha). */
const techdocsAddonsApiRef = createApiRef<{
  getAddons: () => TechDocsAddonOptions[];
}>({
  id: 'plugin.techdocs.addons',
});

const ConfiguredMermaidAddon = () => <Mermaid {...techdocsMermaidProps} />;

const mermaidAddon = AddonBlueprint.make({
  name: 'mermaid',
  params: {
    name: 'Mermaid',
    location: TechDocsAddonLocations.Content,
    component: ConfiguredMermaidAddon,
  },
});

const navScrollFixAddon = AddonBlueprint.make({
  name: 'nav-scroll-fix',
  params: {
    name: 'TechDocsNavScrollFix',
    location: TechDocsAddonLocations.Content,
    component: TechDocsNavScrollFixAddon,
  },
});

function buildAddonElements(addonOptions: TechDocsAddonOptions[]) {
  return addonOptions.map(options => {
    const Addon = options.component;
    attachTechDocsAddonComponentData(Addon, options);
    return (
      <Suspense fallback={null} key={options.name}>
        <Addon />
      </Suspense>
    );
  });
}

/** Custom TechDocs reader; /docs now renders the unified catalog page. */
const techdocsFeature = techdocsPlugin.withOverrides({
  extensions: [
    mermaidAddon,
    navScrollFixAddon,
    techdocsPlugin.getExtension('entity-content:techdocs').override({
      disabled: true,
    }),
    techdocsPlugin.getExtension('page:techdocs').override({
      factory(originalFactory) {
        return originalFactory({
          params: {
            path: '/docs',
            noHeader: true,
            routeRef: legacyTechdocsPlugin.routes.root,
            loader: async () => {
              const { CustomCatalogPage } = await import(
                '../components/catalog/CustomCatalogPage'
              );
              const { DOCUMENTATION_KIND } = await import(
                '../components/catalog/filters/TechDocsEntityFilter'
              );
              // `owned` mirrors the TechDocs home this route replaced; the
              // Document view reached from /catalog keeps the picker default.
              return (
                <CustomCatalogPage
                  initialKind={DOCUMENTATION_KIND}
                  initialUserFilter="owned"
                  isLegacyEntryPoint
                />
              );
            },
          },
        });
      },
    }),
    techdocsPlugin.getExtension('page:techdocs/reader').override({
      factory(originalFactory, { apis, inputs }) {
        const addonsApi = apis.get(techdocsAddonsApiRef);
        return originalFactory({
          params: {
            path: '/docs/:namespace/:kind/:name',
            noHeader: true,
            routeRef: legacyTechdocsPlugin.routes.docRoot,
            loader: async () => {
              const apiAddons = addonsApi?.getAddons() ?? [];
              const directAddons = inputs.addons.map(output =>
                output.get(AddonBlueprint.dataRefs.addon),
              );
              const addons = buildAddonElements([...apiAddons, ...directAddons]);

              const { CustomTechDocsReaderWithAddons } = await import(
                '../components/techdocs/CustomTechDocsReaderPage'
              );

              return <CustomTechDocsReaderWithAddons addons={addons} />;
            },
          },
        });
      },
    }),
  ],
});

export default techdocsFeature;
